import {
    AfterViewInit,
    Component,
    Inject,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { distinctUntilChanged, skip, Subject, takeUntil } from 'rxjs';
import { saveAs } from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SolarService } from 'src/app/services/cadastre-solaire/cadastre-solaire.service';
import { SolarThermalService } from 'src/app/services/cadastre-solaire/cadastre-solaire-thermal.service';
import { UsefulService } from 'src/app/services/UsefulService';
import { CadastreSolaireContactModalComponent } from '../../../../modals/contact/cadastre-solaire-contact-modal.component';
import { CadastreSolaireInfoModalComponent } from '../../../../modals/info/cadastre-solaire-info-modal.component';

import { environment } from 'src/environments/environment';
import { IncomeBracket, ProfileEnum, ThermalResult } from 'src/app/models/Solar';

@Component({
    selector: 'cadastreSolaireThermalSimulation',
    templateUrl: './cadastre-solaire-thermal-simulation.template.html',
    styleUrls: ['./cadastre-solaire-thermal-simulation.component.scss'],
    standalone: false,
})
export class CadastreSolaireThermalSimulationComponent implements OnInit, AfterViewInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @ViewChild('bankLoanCheckboxTemplate', { static: true })
    bankLoanCheckboxTemplate!: TemplateRef<any>;

    public isSimulateBankLoanChecked = false;

    public showLoader: boolean = true;

    public modularity: any;
    public slider: {
        maxModuleCount: number;
        moduleCount: number;
        moduleArea: number;
        stringifiedPeakPower: string;
    };

    public tooltipThInfoDetail: any;
    public tooltipThInvestmentDetail: any;
    public tooltipThInstallationDetail: any;
    public tooltipThProductionDetail: any;
    public tooltipThEconomyDetail: any;

    public htmlThInvestment: string;
    public htmlThInstallation: string;
    public htmlThProduction: string;
    public htmlThEconomy: string;
    public htmlThWarning: string;

    public warningMessageEconomy: string;

    public adjustInstallationSizeLabel: string;
    public contactButtonLabel: string;
    public isResultsDownloading: boolean;

    public titleInvestment: string = 'Investissement';
    public titleProduction: string = 'Production';
    public titleEconomy: string = 'Economies';

    public isOverProductionWarningActive: boolean = false;

    public currentResult: ThermalResult;
    public optimalResult:
        | {
              moduleCount: number;
              stringifiedModuleCount: string;
          }
        | null
        | undefined = null;

    constructor(
        private modalService: NgbModal,
        private notification: ToastrService,
        @Inject(LocalStorageService) private localStorageService: LocalStorageService,
        @Inject(SolarService) public solarService: SolarService,
        @Inject(SolarThermalService) public solarThermalService: SolarThermalService,
        @Inject(UsefulService) private usefulService: UsefulService,
    ) {}

    ngOnInit(): void {
        this.modularity = this.localStorageService.get('preferences').modularity.solar;

        this.tooltipThInfoDetail = this.solarThermalService.thInfo.message.info_detail;
        this.tooltipThInvestmentDetail =
            this.solarThermalService.thInfo.message.investissement_detail;
        this.tooltipThInstallationDetail =
            this.solarThermalService.thInfo.message.installation_detail;
        this.tooltipThProductionDetail = this.solarThermalService.thInfo.message.production_detail;
        this.tooltipThEconomyDetail = this.solarThermalService.thInfo.message.economie_detail;
        this.htmlThWarning = this.solarThermalService.thInfo.message.warning;

        const customAdjustInstallationSizeLabel =
            this.solarService.moduleInfo.ajuster_taille_installation;
        this.adjustInstallationSizeLabel = customAdjustInstallationSizeLabel
            ? customAdjustInstallationSizeLabel
            : "J'ajuste la taille de mon installation";

        const customContactButtonLabel = this.solarService.moduleInfo.contacts_bouton;
        this.contactButtonLabel = customContactButtonLabel
            ? customContactButtonLabel
            : 'Contacter un expert';

        this.solarService.bankLoanResultObs$
            .pipe(skip(1), takeUntil(this.destroy$), distinctUntilChanged())
            .subscribe((bankLoanResult) => {
                if (bankLoanResult) {
                    const newRoi = bankLoanResult.roi;
                    const newInvestment = bankLoanResult.investment;
                    const interest = bankLoanResult.interest;
                    const originalRoi = this.solarThermalService.originalRoi;

                    this.solarThermalService.currentResult.roi = newRoi;
                    this.solarThermalService.currentResult.investment = newInvestment;
                    this.solarThermalService.roi = newRoi;
                    this.solarThermalService.investment = newInvestment;

                    this._updateThInvestment(interest);
                    this._updateThEconomy(originalRoi);

                    if (!this.modularity.simulations) {
                        this.solarService.closeSimulateBankLoan();
                    } else {
                        this.validate();
                        this.solarService.closeSimulateBankLoan();
                    }
                }
            });
    }

    async ngAfterViewInit(): Promise<void> {
        await this.initThermalSimulation();

        const maxResult = this.solarThermalService.getMaxModuleResult();
        const minResult = this.solarThermalService.getMinModuleResult();
        const optimalResult = this.solarThermalService.findOptimalResult();

        if (optimalResult) {
            this.optimalResult = {
                moduleCount: optimalResult.moduleCount,
                stringifiedModuleCount: this.usefulService.stringifyNumber(
                    optimalResult.moduleCount,
                ),
            };
        }

        const initResult = optimalResult ? optimalResult : minResult;
        this.solarThermalService.setCurrentResult(initResult);
        this.currentResult = initResult;

        this.slider = {
            maxModuleCount: maxResult.moduleCount,
            moduleCount: initResult.moduleCount,
            moduleArea: this.usefulService.round(initResult.totalModuleArea),
            stringifiedPeakPower: this.usefulService.stringifyNumber(
                this.usefulService.ceil(initResult.peakPower, 1),
            ),
        };

        this._updateThIndicator();

        const isNew = this.solarService.checkIfSelectedElementsIsNew();
        if (isNew) {
            this.solarService.setTotalModuleAreaToSelectionInfo(maxResult.totalModuleArea);
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async initThermalSimulation() {
        try {
            this.showLoader = true;
            await this.solarThermalService.simulateThermal();
        } catch (error) {
            console.error('Error initThermalSimulation', error);
            this.notification.error(
                "Une erreur est survenue. Impossible d'initialiser la simulation thermique.",
            );
            this.solarService.updateMainStage('technologyStage');
        } finally {
            this.showLoader = false;
        }
    }

    async downloadPdf() {
        try {
            this.isResultsDownloading = true;

            const results = {
                technology: 'thermal',
                htmlInvestment: this.htmlThInvestment,
                htmlInstallation: this.htmlThInstallation,
                htmlProduction: this.htmlThProduction,
                htmlEconomy: this.htmlThEconomy,

                moduleCount: this.slider.moduleCount,
                showerCount: this.solarThermalService.totalShowerCount,
                moduleArea: this.solarThermalService.totalModuleArea,
                investment: this.solarThermalService.investment,
                production: this.solarThermalService.totalProduction,
                economy: this.solarThermalService.totalSaving,
                roi: this.solarThermalService.roi,
            };

            const buffer = await this.solarService.getSolarPdfFile(results);
            const blob = new Blob([buffer], { type: 'application/pdf' });
            saveAs(blob, 'solaire-thermique.pdf');
        } catch (error) {
            console.error('Error downloadPdf', error);
            this.notification.error(
                'Une erreur est survenue. Impossible de télécharger les résultats.',
            );
        } finally {
            this.isResultsDownloading = false;
        }
    }

    openContactModal() {
        const profileName = environment.name;

        if (profileName === 'cadastre_esterel') {
            window.open(
                'https://france-renov.gouv.fr/annuaires-professionnels/artisan-rge-architecte#/tab',
                '_blank',
            );
        } else {
            const modalRef = this.modalService.open(CadastreSolaireContactModalComponent, {
                modalDialogClass: profileName,
            });
            modalRef.result.then(
                (result) => {},
                () => {},
            );
        }
    }

    cancel() {
        const profile = this.solarService.getProfile();

        if (profile === ProfileEnum.INDIVIDUAL) {
            this.solarService.updateThermalStage('thermalBuildingDescriptionStage');
        } else {
            this.solarService.updateMainStage('technologyStage');
        }
    }

    validate() {
        const result = {
            technology: 'thermal',
            label: 'Thermique',
            htmlInvestment: this.htmlThInvestment,
            htmlInstallation: this.htmlThInstallation,
            htmlProduction: this.htmlThProduction,
            htmlEconomy: this.htmlThEconomy,

            moduleCount: this.slider.moduleCount,
            showerCount: this.solarThermalService.totalShowerCount,
            moduleArea: this.solarThermalService.totalModuleArea,
            investment: this.solarThermalService.investment,
            production: this.solarThermalService.totalProduction,
            economy: this.solarThermalService.totalSaving,
            roi: this.solarThermalService.roi,
        };

        this.solarService.addResult(result);
        this.solarService.updateMainStage('resultsStage');
    }

    updateModuleCountByBuilding() {
        const result = this.solarThermalService.findResultByModuleCount(this.slider.moduleCount);
        if (!result) {
            return;
        }

        this.solarThermalService.setCurrentResult(result);
        this.currentResult = result;

        this.slider.moduleCount = result.moduleCount;
        this.slider.moduleArea = this.usefulService.round(result.totalModuleArea);
        this.slider.stringifiedPeakPower = this.usefulService.stringifyNumber(
            this.usefulService.ceil(result.peakPower, 1),
        );

        this._updateThIndicator();
    }

    private _updateThIndicator() {
        this._updateThInvestment();
        this._updateThInstallation();
        this._updateThProduction();
        this._updateThEconomy();
    }

    private _updateThInvestment(interest?: number) {
        let html = this.solarThermalService.thInfo.message.investissement;
        const isIncomeBracketDefined = !!this.solarThermalService.incomeBracket;

        const splitByInvestmentTag = html.split('##th_investment');
        if (splitByInvestmentTag.length === 2) {
            let tag = `<span class="text-st-active">${this._stringifyInvestment()}</span>`;
            html = splitByInvestmentTag.join(tag);
        }

        if (!interest) {
            if (isIncomeBracketDefined) {
                if (this.solarThermalService.incomeBracketLabel === IncomeBracket.NO_ANSWER) {
                    html += `. Des aides peuvent être disponibles pour ce projet. Renseignez-vous auprès d'un professionnel ou des organismes compétents.`;
                } else {
                    html += `. Vous pourriez bénéficier d'aides de l'ordre de <span class="text-st-active">${this._stringifyHelp()}</span> (<span class="text-st-active">${this.solarThermalService.incomeBracketLabel}</span>) 
                            soit un investissement de <span class="text-st-active">${this._stringifyNetInvestment()}</span> après déduction de ces aides.`;
                }
            } else {
                const splitByInterestTag = html.split('##th_interest');
                if (splitByInterestTag.length === 2) {
                    html = splitByInterestTag.join('');
                }
            }
        } else {
            if (isIncomeBracketDefined) {
                if (this.solarThermalService.incomeBracketLabel === IncomeBracket.NO_ANSWER) {
                    html += `, dont <span class="text-st-active">${this._stringifyInterest(interest)}</span> d'intérêts d'emprunt bancaire. Des aides peuvent être disponibles pour ce projet. Renseignez-vous auprès d'un professionnel ou des organismes compétents.`;
                } else {
                    html += ` et dont <span class="text-st-active">${this._stringifyInterest(interest)}</span> d’intérêt bancaire. 
                    Vous pourriez bénéficier d'aides de l'ordre de <span class="text-st-active">${this._stringifyHelp()}</span> (<span class="text-st-active">${this.solarThermalService.incomeBracketLabel}</span>).`;
                }
            } else {
                const splitByInterestTag = html.split('##th_interest');
                if (splitByInterestTag.length === 2) {
                    let tag = `, dont <span class="text-st-active">${this._stringifyInterest(interest)}</span> d'intérêts d'emprunt bancaire`;
                    html = splitByInterestTag.join(tag);
                }
            }
        }

        html = `<span>${html}</span>`;
        this.htmlThInvestment = html;
    }

    private _updateThInstallation() {
        let html = this.solarThermalService.thInfo.message.installation;

        // ##th_module_count
        const isThModuleCountDefined = html.includes('##th_module_count');
        if (isThModuleCountDefined) {
            const placeholder = `<span class="text-st-active">${this._stringifyTotalModuleCount()}</span>`;
            html = html.replace('##th_module_count', placeholder);
        }

        // ##th_total_module_area
        const isThModuleAreaDefined = html.includes('##th_total_module_area');
        if (isThModuleAreaDefined) {
            const placeholder = `<span class="text-st-active">${this._stringifyModuleArea()}</span>`;
            html = html.replace('##th_total_module_area', placeholder);
        }

        html = `<span>${html}</span>`;
        this.htmlThInstallation = html;
    }

    private _updateThProduction() {
        // ##th_production_by_year
        const splitByProductionTag =
            this.solarThermalService.thInfo.message.production.split('##th_production_by_year');
        if (splitByProductionTag.length === 2) {
            let html = splitByProductionTag.join(
                `<span class="text-st-active">${this._stringifyProduction()}</span>`,
            );

            // ##th_volume_by_year
            const splitByVolumeByYearTag = html.split('##th_volume_by_year');
            if (splitByVolumeByYearTag.length === 2) {
                html = splitByVolumeByYearTag.join(
                    `<span class="text-st-active">${this._stringifyVolumeByYear()} L/an</span>`,
                );
            }

            // ##th_volume_by_day
            const splitByVolumeByDayTag = html.split('##th_volume_by_day');
            if (splitByVolumeByDayTag.length === 2) {
                html = splitByVolumeByDayTag.join(
                    `<span class="text-st-active">${this._stringifyVolumeByDay()} L/jour</span>`,
                );
            }

            // ##th_equivalent_person
            const splitByEqPersonTag = html.split('##th_equivalent_person');
            if (splitByEqPersonTag.length === 2) {
                const equivalentPerson = this.usefulService.round(
                    this.solarThermalService.equivalentPerson,
                    1,
                );
                let unit = 'personne';
                equivalentPerson > 1 ? (unit += 's') : unit;
                const equivalentPersonStringified =
                    this.usefulService.stringifyNumber(equivalentPerson);

                html = splitByEqPersonTag.join(
                    `<span class="text-st-active">${equivalentPersonStringified} ${unit}</span>`,
                );
            }

            // ##shower_count
            const splitByShowerTag = html.split('##shower_count');
            if (splitByShowerTag.length === 2) {
                const thShowerCount = this.solarThermalService.totalShowerCount;
                let unit = 'douche';
                thShowerCount > 1 ? (unit += 's') : unit;

                html = splitByShowerTag.join(
                    `<span class="text-st-active">${this._stringifyShowerCount()} ${unit}</span>`,
                );
            }

            // ##person_count
            const splitByPersonTag = html.split('##person_count');
            if (splitByPersonTag.length === 2) {
                const thPersonCount = this.solarThermalService.totalPersonCount;
                let unit = 'personne';
                thPersonCount > 1 ? (unit += 's') : unit;
                const thPersonCountStringified = this.usefulService.stringifyNumber(thPersonCount);

                html = splitByPersonTag.join(
                    `<span class="text-st-active">${thPersonCountStringified} ${unit}</span>`,
                );
            }

            html = `<span>${html}</span>`;
            this.htmlThProduction = html;
        }
    }

    private _stringifyProduction() {
        const thProduction = Math.round(this.solarThermalService.totalProduction);
        const thProductionStingified = this.usefulService.stringifyNumber(thProduction);
        return `${thProductionStingified}\u00a0kWh/an`;
    }

    private _stringifyVolumeByYear() {
        const volumeByYear = this.solarThermalService.volumeByYear;
        const volumeByYearStringified = this.usefulService.stringifyNumber(volumeByYear);
        return `${volumeByYearStringified}`;
    }

    private _stringifyVolumeByDay() {
        const volumeByDay = this.solarThermalService.volumeByDay;
        const volumeByDayStringified = this.usefulService.stringifyNumber(volumeByDay);
        return `${volumeByDayStringified}`;
    }

    private _stringifyShowerCount() {
        const thShowerCount = this.solarThermalService.totalShowerCount;
        const thShowerCountStringified = this.usefulService.stringifyNumber(thShowerCount);
        return `${thShowerCountStringified}`;
    }

    private _updateThEconomy(originalRoi?: number) {
        // ##th_saving_by_year
        const splitBySavingTag =
            this.solarThermalService.thInfo.message.economie.split('##th_saving_by_year');
        if (splitBySavingTag.length === 2) {
            let html = splitBySavingTag.join(
                `<span class="text-st-active">${this._stringifyEconomy()}</span>`,
            );

            // ##th_roi
            const splitByRoiTag = html.split('##th_roi');
            if (splitByRoiTag.length === 2) {
                // html = splitByRoiTag.join(
                //     `<span class="text-st-active">${this._stringifyRoi()}</span>`,
                // );
                let tag = `<span class="text-st-active">${this._stringifyRoi()}</span>`;
                if (originalRoi) {
                    tag += ` (sans emprunt bancaire le temps de retour sur investissement est de <span class="text-st-active">${this._stringifyRoi(originalRoi)}</span>)`;
                }
                html = splitByRoiTag.join(tag);
            }
            html = `<span>${html}</span>`;
            this.htmlThEconomy = html;
        }
    }

    private _stringifyTotalModuleCount() {
        const thTotalModuleCount = Math.round(this.solarThermalService.totalModuleCount);
        const thTotalModuleCountStingified = this.usefulService.stringifyNumber(thTotalModuleCount);
        const unit = thTotalModuleCount == 1 ? 'panneau' : 'panneaux';
        return `${thTotalModuleCountStingified}\u00a0${unit}`;
    }

    private _stringifyModuleArea() {
        const thInstallationArea = Math.round(this.solarThermalService.totalModuleArea);
        const thInstallationAreaStingified = this.usefulService.stringifyNumber(thInstallationArea);
        return `${thInstallationAreaStingified}\u00a0m²`;
    }

    private _stringifyInvestment() {
        const thInvestment = Math.round(this.solarThermalService.investment);
        const thInvestmentStingified = this.usefulService.stringifyNumber(thInvestment);
        return `${thInvestmentStingified}\u00a0€`;
    }

    private _stringifyInterest(interest: number) {
        const interestStringified = this.usefulService.stringifyNumber(Math.round(interest));
        return `${interestStringified}\u00a0€`;
    }

    private _stringifyHelp() {
        const help = Math.round(this.solarThermalService.help);
        const helpStringified = this.usefulService.stringifyNumber(help);
        return `${helpStringified}\u00a0€`;
    }

    private _stringifyNetInvestment() {
        const netInvestment = Math.round(this.solarThermalService.netInvestment);
        const netInvestmentStringified = this.usefulService.stringifyNumber(netInvestment);
        return `${netInvestmentStringified}\u00a0€`;
    }

    private _stringifyEconomy() {
        const thSaving = Math.round(this.solarThermalService.totalSaving);
        const thSavingStingified = this.usefulService.stringifyNumber(thSaving);
        return `${thSavingStingified}\u00a0€/an`;
    }

    private _stringifyRoi(roi?: number) {
        const thRoi = roi ? Math.round(roi) : Math.round(this.solarThermalService.roi);
        let unit = 'an';
        thRoi > 1 ? (unit += 's') : unit;
        const thRoiStingified = this.usefulService.stringifyNumber(thRoi);

        this.warningMessageEconomy = this.solarService.setWarningRoi(thRoi);

        return `${thRoiStingified} ${unit}`;
    }

    openInfoModal(body: string, title: string) {
        this.modalService.dismissAll();
        const modalRef = this.modalService.open(CadastreSolaireInfoModalComponent, { size: 'xs' });
        modalRef.componentInstance.body = body;
        modalRef.componentInstance.title = title;
    }

    get bankLoanTemplateOrNull(): TemplateRef<any> | null {
        if (!this.modularity.simulationBankLoan) return null;
        if (!this.solarThermalService.netInvestment) return null;
        return this.bankLoanCheckboxTemplate;
    }

    public simulateBankLoan() {
        this.solarService.simulateBankLoan();
    }

    get mainButtonLabel(): string {
        if (!this.modularity.simulations) {
            return this.contactButtonLabel;
        }

        return 'Valider ma simulation';
    }

    get mainButtonAction(): () => void {
        if (!this.modularity.simulations) {
            return () => this.openContactModal();
        }

        if (this.isSimulateBankLoanChecked) {
            return () => this.simulateBankLoan();
        }

        return () => this.validate();
    }
}
